import { useEffect, useRef, useState } from "react";
import { api, API } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Upload } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fields = [
  ["business_name", "Business name"],
  ["gstin", "GSTIN"],
  ["phone", "Phone"],
  ["email", "Email"],
  ["state", "State"],
  ["state_code", "State code"],
  ["invoice_prefix", "Invoice prefix"],
  ["upi_id", "UPI ID"],
];

export default function Settings() {
  const [s, setS] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => { api.get("/settings").then(r=>setS(r.data)); }, []);
  const AUTO_DEFAULTS = { reminder_start_after_days: 0, reminder_repeat_days: 3, reminder_max_times: 0, reminder_check_hours: 6, review_delay_hours: 24, review_max_times: 1, review_repeat_hours: 48 };
  const save = async () => {
    // empty number boxes fall back to their default; repeat gaps and counts must be at least 1
    const body = { ...s };
    Object.entries(AUTO_DEFAULTS).forEach(([k, d]) => { if (body[k] === "" || body[k] == null) body[k] = d; });
    ["reminder_repeat_days", "reminder_check_hours", "review_max_times", "review_repeat_hours"].forEach((k) => { if (body[k] < 1) body[k] = 1; });
    try { await api.put("/settings", body); setS(body); toast.success("Settings saved"); }
    catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : e?.response?.status === 422 ? "Please check the numbers you entered" : "Admin only");
    }
  };
  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS({ ...s, logo_url: data.logo_url });
      toast.success("Logo updated");
    } catch { toast.error("Upload failed (admin only)"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!s) return <div className="text-muted-foreground">Loading…</div>;
  const logoSrc = s.logo_url ? (s.logo_url.startsWith("http") ? s.logo_url : `${BACKEND}${s.logo_url}?t=${Date.now()}`) : null;

  const num = (k, label, def, hint) => (
    <div key={k}>
      <Label>{label}</Label>
      <Input type="number" min="0" value={s[k] ?? def} onChange={e=>setS({...s,[k]: e.target.value === "" ? "" : Number(e.target.value)})} data-testid={`settings-${k}`}/>
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Configure</p>
        <h1 className="font-serif-elegant text-5xl mt-1">Settings</h1>
      </div>

      <Card className="p-6 bg-white border-border space-y-4">
        <h3 className="font-serif-elegant text-2xl">Brand & Logo</h3>
        <div className="flex items-start gap-6 flex-wrap">
          <div className="w-40 h-40 rounded-md border hairline bg-secondary/40 flex items-center justify-center overflow-hidden">
            {logoSrc ? <img src={logoSrc} alt="logo" className="max-w-full max-h-full object-contain"/> : <span className="text-xs text-muted-foreground">No logo</span>}
          </div>
          <div className="flex-1 min-w-[240px] space-y-3">
            <div>
              <Label>Tagline (shown on invoice)</Label>
              <Input value={s.tagline||""} onChange={e=>setS({...s,tagline:e.target.value})} data-testid="settings-tagline"/>
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={uploadLogo} data-testid="logo-file-input"/>
            <Button variant="outline" onClick={()=>fileRef.current?.click()} data-testid="upload-logo-btn"><Upload size={14} className="mr-2"/>Upload New Logo</Button>
            <p className="text-xs text-muted-foreground">PNG, JPG or WebP · Recommended: transparent background, min 400px wide.</p>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-white border-border space-y-4">
        <h3 className="font-serif-elegant text-2xl">Business details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map(([k,label]) => (
            <div key={k}>
              <Label>{label}</Label>
              <Input value={s[k]||""} onChange={e=>setS({...s,[k]:e.target.value})} data-testid={`settings-${k}`}/>
            </div>
          ))}
        </div>
        <div><Label>Address</Label><Textarea rows={2} value={s.address||""} onChange={e=>setS({...s,address:e.target.value})}/></div>
        <div><Label>Bank details</Label><Textarea rows={2} value={s.bank_details||""} onChange={e=>setS({...s,bank_details:e.target.value})}/></div>
        <div><Label>Terms</Label><Textarea rows={2} value={s.terms||""} onChange={e=>setS({...s,terms:e.target.value})}/></div>
        <Button onClick={save} className="bg-primary hover:bg-[#984D38]" data-testid="settings-save">Save Settings</Button>
      </Card>

      <Card className="p-6 bg-white border-border">
        <h3 className="font-serif-elegant text-2xl mb-2">WhatsApp Business API</h3>
        <p className="text-sm text-muted-foreground">Credentials are configured server-side in <code>backend/.env</code>. Set <code>WHATSAPP_PHONE_NUMBER_ID</code> and <code>WHATSAPP_ACCESS_TOKEN</code> (permanent system user token) then restart backend. Until configured, sends will be logged as mock.</p>
      </Card>

      <Card className="p-6 bg-white border-border space-y-6" data-testid="automation-card">
        <div>
          <h3 className="font-serif-elegant text-2xl mb-1">Automation timing</h3>
          <p className="text-sm text-muted-foreground">Choose when each WhatsApp message goes out and how many times. Click Save automation at the bottom. You can also send reminders by hand from <strong>Reports → Outstanding</strong>.</p>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-2 font-medium"><input type="checkbox" className="h-4 w-4" checked={s.reminder_enabled ?? true} onChange={e=>setS({...s,reminder_enabled:e.target.checked})}/>Payment reminder (overdue invoices)</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {num("reminder_start_after_days", "First reminder, days after due date", 0, "0 = as soon as it is overdue")}
            {num("reminder_repeat_days", "Repeat every (days)", 3, "Gap between reminders for one invoice")}
            {num("reminder_max_times", "Maximum reminders per invoice", 0, "0 = keep going until paid")}
            {num("reminder_check_hours", "Check for overdue invoices every (hours)", 6, "How often the server looks")}
          </div>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-2 font-medium"><input type="checkbox" className="h-4 w-4" checked={s.review_enabled ?? true} onChange={e=>setS({...s,review_enabled:e.target.checked})}/>Review / Rate us request</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {num("review_delay_hours", "Send after invoice is created (hours)", 24, "Applies to new invoices")}
            {num("review_max_times", "Number of times to send", 1, "1 = only once")}
            {num("review_repeat_hours", "Repeat every (hours)", 48, "Used only when sending more than once")}
          </div>
        </div>

        <Button onClick={save} className="bg-primary hover:bg-[#984D38]" data-testid="automation-save">Save automation</Button>
      </Card>
    </div>
  );
}
